/**
 * Subscription system constants
 */

// Trial duration
export const TRIAL_DURATION_DAYS = 14;

// Usage limits by subscription tier
export const SUBSCRIPTION_LIMITS = {
  FREE: {
    dailySpellCheckLimit: 10,
    dailyAIAnalysisLimit: 10, // OpenRouter proxy limit (10/day). BYOK is unlimited.
    maxTextLength: 500, // Spell check only
    canUseUnlimited: false
  },
  TRIAL: {
    dailySpellCheckLimit: -1, // Unlimited during trial
    dailyAIAnalysisLimit: 50, // OpenRouter proxy limit (50/day). BYOK is unlimited.
    maxTextLength: 10000, // Same as premium - full experience during trial
    canUseUnlimited: true
  },
  BETA: {
    dailySpellCheckLimit: -1, // Unlimited during beta
    dailyAIAnalysisLimit: 100, // OpenRouter proxy limit (100/day). BYOK is unlimited.
    maxTextLength: 10000, // Same as premium for beta users
    canUseUnlimited: true
  },
  PREMIUM: {
    dailySpellCheckLimit: -1, // Unlimited
    dailyAIAnalysisLimit: 500, // OpenRouter proxy limit (500/day). BYOK is unlimited.
    maxTextLength: 10000, // Spell check only
    canUseUnlimited: true
  }
} as const;

// Feature access by subscription tier
export const FEATURE_ACCESS = {
  FREE: {
    spellCheck: true,
    aiAnalysis: true, // OpenRouter 10/day. BYOK unlimited with own API key.
    unlimitedLength: false,
    exportFeatures: false,
    customDictionary: false,
    canUseBYOK: true // Can use own API keys
  },
  TRIAL: {
    spellCheck: true,
    aiAnalysis: true,
    unlimitedLength: true,
    exportFeatures: true,
    customDictionary: true,
    canUseBYOK: true
  },
  BETA: {
    spellCheck: true,
    aiAnalysis: true,
    unlimitedLength: true,
    exportFeatures: true,
    customDictionary: true,
    canUseBYOK: true
  },
  PREMIUM: {
    spellCheck: true,
    aiAnalysis: true,
    unlimitedLength: true,
    exportFeatures: true,
    customDictionary: true,
    canUseBYOK: true
  }
} as const;

// Pricing plans
export const PRICING_PLANS = {
  FREE: {
    id: 'free',
    name: '무료',
    price: 0,
    currency: 'USD',
    interval: 'monthly' as const,
    features: [
      `하루 ${SUBSCRIPTION_LIMITS.FREE.dailySpellCheckLimit}회 맞춤법 검사`,
      `맞춤법 검사 ${SUBSCRIPTION_LIMITS.FREE.maxTextLength.toLocaleString()}자(회당)`,
      `AI 기능 ${SUBSCRIPTION_LIMITS.FREE.dailyAIAnalysisLimit}회/일`
    ],
    limits: SUBSCRIPTION_LIMITS.FREE
  },
  PREMIUM_MONTHLY: {
    id: 'premium_monthly',
    name: '프리미엄 월간',
    price: 3.3,
    currency: 'USD',
    interval: 'monthly' as const,
    features: [
      '무제한 맞춤법 검사',
      `맞춤법 검사 ${SUBSCRIPTION_LIMITS.PREMIUM.maxTextLength.toLocaleString()}자(회당)`,
      `AI 기능 ${SUBSCRIPTION_LIMITS.PREMIUM.dailyAIAnalysisLimit.toLocaleString()}회/일`
    ],
    limits: SUBSCRIPTION_LIMITS.PREMIUM,
    isPopular: true
  },
  PREMIUM_YEARLY: {
    id: 'premium_yearly',
    name: '프리미엄 연간',
    price: 29.99,
    currency: 'USD',
    interval: 'yearly' as const,
    features: [
      '무제한 맞춤법 검사',
      `맞춤법 검사 ${SUBSCRIPTION_LIMITS.PREMIUM.maxTextLength.toLocaleString()}자(회당)`,
      `AI 기능 ${SUBSCRIPTION_LIMITS.PREMIUM.dailyAIAnalysisLimit.toLocaleString()}회/일`
    ],
    limits: SUBSCRIPTION_LIMITS.PREMIUM,
    isPopular: true
  }
} as const;

// StoreKit product IDs (for future Mac App Store integration)
export const STOREKIT_PRODUCT_IDS = {
  MONTHLY: 'com.hyungyunlim.korean_grammar_advanced.premium.monthly',
  YEARLY: 'com.hyungyunlim.korean_grammar_advanced.premium.yearly'
} as const;

// License validation
export const LICENSE_CONFIG = {
  GUMROAD_API_URL: 'https://api.gumroad.com/v2/licenses/verify',
  VALIDATION_INTERVAL_MS: 24 * 60 * 60 * 1000, // 24 hours
  OFFLINE_GRACE_PERIOD_DAYS: 7,
  MAX_DEVICES: 5 // 모든 플랜 5대로 통일
} as const;

// Reset time for daily usage (0 = midnight)
export const USAGE_RESET_HOUR = 0;

// Storage keys
export const STORAGE_KEYS = {
  TRIAL_INFO: 'subscription_trial_info',
  USAGE_RECORD: 'subscription_usage_record',
  LICENSE_KEY: 'subscription_license_key',
  SUBSCRIPTION_STATUS: 'subscription_status',
  LAST_VALIDATION: 'subscription_last_validation'
} as const;